import 'vtk.js/Sources/Rendering/Profiles/All';

import vtkFullScreenRenderWindow from 'vtk.js/Sources/Rendering/Misc/FullScreenRenderWindow';
import vtkHttpSceneLoader from 'vtk.js/Sources/IO/Core/HttpSceneLoader';

const RESULT_FIELD_KEYWORDS = [
    'von',
    'vm',
    'stress',
    'strain',
    'sigma',
    'eps',
    'principal',
    'shear',
    'plastic',
    'effective',
    'equivalent',
];

const rootContainer = document.querySelector('.biosol-canvas');
if (!rootContainer) {
    throw new Error('Missing .biosol-canvas container for vtk scene rendering.');
}

const containerStyle = {
    margin: '0',
    padding: '0',
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
};

const fullScreenRenderer = vtkFullScreenRenderWindow.newInstance({
    rootContainer,
    containerStyle,
});
const renderer = fullScreenRenderer.getRenderer();
const renderWindow = fullScreenRenderer.getRenderWindow();
const sceneImporter = vtkHttpSceneLoader.newInstance({
    renderer,
    fetchGzip: false,
});

function getRangeForArray(array) {
    const numberOfComponents = array.getNumberOfComponents();
    const componentIndex = numberOfComponents === 1 ? 0 : -1;
    const range = array.getRange(componentIndex);
    if (!range || range.length !== 2) {
        return null;
    }
    if (!Number.isFinite(range[0]) || !Number.isFinite(range[1])) {
        return null;
    }
    return range;
}

function isDirectColorArray(array) {
    const numberOfComponents = array.getNumberOfComponents();
    const dataType = array.getDataType();
    return (
        (numberOfComponents === 3 || numberOfComponents === 4) &&
        (dataType === 'Uint8Array' || dataType === 'Uint8ClampedArray')
    );
}

function scoreArrayForResults(name, array) {
    let score = 0;
    const normalizedName = name.toLowerCase();

    if (RESULT_FIELD_KEYWORDS.some((keyword) => normalizedName.includes(keyword))) {
        score += 100;
    }

    if (array.getNumberOfComponents() === 1) {
        score += 30;
    }

    if (isDirectColorArray(array)) {
        score -= 50;
    }

    return score;
}

function getNamedArrays(dataSetAttributes) {
    return dataSetAttributes
        .getArrays()
        .map((array) => ({ array, name: array?.getName() || '' }))
        .filter(({ name }) => Boolean(name));
}

function pickBestArray(pointData, cellData) {
    const candidates = [];

    getNamedArrays(pointData).forEach(({ array, name }) => {
        candidates.push({
            association: 'point',
            array,
            name,
            score: scoreArrayForResults(name, array),
        });
    });

    getNamedArrays(cellData).forEach(({ array, name }) => {
        candidates.push({
            association: 'cell',
            array,
            name,
            score: scoreArrayForResults(name, array),
        });
    });

    if (!candidates.length) {
        return null;
    }

    candidates.sort((a, b) => b.score - a.score);
    return candidates[0];
}

function applyScalarColoring(sceneItem) {
    const dataSet = sceneItem.source?.getOutputData();
    if (!dataSet) {
        return;
    }

    const pointData = dataSet.getPointData();
    const cellData = dataSet.getCellData();
    const selected = pickBestArray(pointData, cellData);
    const pointArrayNames = getNamedArrays(pointData).map(({ name }) => name);
    const cellArrayNames = getNamedArrays(cellData).map(({ name }) => name);

    if (!selected) {
        console.warn(
            `[VTK] No result arrays found. Point arrays: [${pointArrayNames.join(', ')}], cell arrays: [${cellArrayNames.join(', ')}]. Export stress/strain arrays into the vtk.js dataset.`
        );
        return;
    }

    const mapper = sceneItem.mapper;
    mapper.setScalarVisibility(true);
    mapper.setColorByArrayName(selected.name);

    if (selected.association === 'point') {
        mapper.setScalarModeToUsePointFieldData();
    } else {
        mapper.setScalarModeToUseCellFieldData();
    }

    if (isDirectColorArray(selected.array)) {
        mapper.setColorModeToDirectScalars();
    } else {
        mapper.setColorModeToMapScalars();
        const range = getRangeForArray(selected.array);
        if (range) {
            mapper.setScalarRange(range[0], range[1]);
            mapper.setUseLookupTableScalarRange(true);
            const lookupTable = mapper.getLookupTable();
            if (lookupTable) {
                lookupTable.setMappingRange(range[0], range[1]);
                lookupTable.build();
            }
        }
    }

    console.info(
        `[VTK] Rendering with ${selected.association} array "${selected.name}" (${selected.array.getNumberOfComponents()} components).`
    );
}

sceneImporter.setUrl('./model/scene');
sceneImporter.onReady(() => {
    sceneImporter.getScene().forEach(applyScalarColoring);
    renderWindow.render();
});
